import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * One-time modal that appears when a logged-in user has no gender set.
 * Locks in a male/female choice via PATCH /auth/me — no cancel/dismiss.
 * The NITEWIRE welcome bot is exempt (it has is_bot=true and no page context).
 */
export default function GenderPrompt() {
  const { user, setUser } = useAuth();
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);

  // Only show for real, authenticated users who don't have a gender yet.
  if (!user || user === false) return null;
  if (user.is_bot) return null;
  if (user.gender === "male" || user.gender === "female") return null;

  const submit = async () => {
    if (!choice || busy) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", { gender: choice });
      setUser(data);
      toast.success(`Saved — you'll appear as ${choice === "male" ? "♂" : "♀"}`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-obsidian/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="gender-prompt-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md bg-obsidian border-2 border-acid p-6 md:p-8 relative">
        <div className="font-mono text-[10px] tracking-widest text-acid mb-2">// QUICK ONE</div>
        <h2 className="font-heading text-3xl md:text-4xl leading-none">
          HOW SHOULD WE<br /><span className="text-acid">SHOW YOU?</span>
        </h2>
        <p className="font-mono text-[11px] text-white/50 mt-3 leading-relaxed">
          Pick a badge that appears next to your handle in chat. You can change it later on your profile.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-0" data-testid="gender-prompt-choices">
          {[
            { value: "male", label: "\u2642 MALE" },
            { value: "female", label: "\u2640 FEMALE" },
          ].map((g) => (
            <button
              key={g.value}
              type="button"
              data-testid={`gender-prompt-${g.value}`}
              onClick={() => setChoice(g.value)}
              className={`font-mono text-xs tracking-widest uppercase py-4 border-2 border-white/20 transition-colors ${
                choice === g.value
                  ? "bg-acid text-obsidian border-acid"
                  : "text-white/70 hover:text-white hover:border-white/40"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={!choice || busy}
          data-testid="gender-prompt-save"
          className="btn-acid w-full mt-6 disabled:opacity-50"
        >
          {busy ? "SAVING…" : "CONFIRM"}
        </button>
      </div>
    </div>
  );
}
